import { Close, CopyAllRounded, Save, SaveRounded, SendRounded } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import { Badge, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, MenuItem, Select, Stack, Typography, useTheme } from '@mui/material'
import React, { useState } from 'react'
import ApiClient from '../../../backend/ApiClient'
import API_URLS from '../../../backend/ApiUrls'
import { IApprovalRequest } from '../../../definitions/Approval'
import { IContractSchema } from '../../../definitions/ContractSchema'
import { IUserRole } from '../../../definitions/Permissions'
import Formatter from '../../../helpers/Formatter'
import ApprovalManager from '../../../managers/ApprovalManager'
import { useAppDispatch } from '../../../store/Hooks'
import { changeRoute } from '../../../store/slices/SessionSlice'
import useCommand from '../../hooks/useCommand'
import useInvalidate from '../../hooks/useInvalidate'
import useQuery, { buildQueryKey } from '../../hooks/useQuery'
import { snackbar } from '../../services/SnackbarService'

interface P {
    open: boolean
    onClose: () => void
    schema: IContractSchema
    onSave: () => void
    onAddVersion: (schema: IContractSchema) => void
    approval: IApprovalRequest
    isSubmitting?: boolean
    isDirty?: boolean
    saveStatus?: string
    statusAddVersion?: string
}

const SchemaSubmitModal: React.FC<P> = ({ open, onClose, schema, onSave, approval, isSubmitting, onAddVersion, isDirty, saveStatus, statusAddVersion }) => {
    const dispatch = useAppDispatch()
    const theme = useTheme()
    const [selectedGroupUUID, setSelectedGroupUUID] = useState<IUserRole['uuid']>()
    const invalidateSchemaApprovalStatus = useInvalidate({ queryKey: buildQueryKey('entity-approval/*') })
    const invalidateSchemaListSmartTable = useInvalidate({ queryKey: buildQueryKey(API_URLS.os.getAllContractSchemas + '/*') })
    const invalidateSchemaData = useInvalidate({ queryKey: buildQueryKey('get-schema', schema.uuid) })

    const { execute: executeSendApprovalToReview, status: statusRequestedApproval } = useCommand({
        commandFn: (approvalReq: IApprovalRequest) => {
            if (!approvalReq || !approvalReq.uuid) {
                console.error('Approval request data or UUID is missing for approval request.')
                return Promise.reject(new Error('Missing approval data for request.'))
            }
            return ApiClient.sendApprovalToReview(approvalReq.uuid, selectedGroupUUID)
        },
        onFinish: result => {
            if (result.response) {
                invalidateSchemaData()
                invalidateSchemaApprovalStatus()
                invalidateSchemaListSmartTable()
                snackbar.show({ severity: 'success', text: 'Schema saved and sent to review' })
            } else {
                snackbar.show({ severity: 'error', text: 'Failed to send contract for review.' })
            }
        }
    })

    const { execute: executeBackToDraft, status: statusBackToDraftApproval } = useCommand({
        commandFn: (approvalReq: IApprovalRequest) => {
            if (!approvalReq || !approvalReq.uuid) {
                console.error('Approval request data or UUID is missing for approval request.')
                return Promise.reject(new Error('Missing approval data for request.'))
            }
            return ApiClient.revertApprovalToDraft(approvalReq.uuid)
        },
        onFinish: result => {
            if (result.response) {
                invalidateSchemaData()
                invalidateSchemaApprovalStatus()
                invalidateSchemaListSmartTable()
                snackbar.show({ severity: 'success', text: 'Schema sent back to Draft' })
            } else {
                snackbar.show({ severity: 'error', text: 'Failed to sent back to Draft schema' })
            }
        }
    })

    const { response: groups } = useQuery({
        queryKey: buildQueryKey('get-approvals-groups'),
        queryFn: () => ApiClient.getApprovalGroups(['schemas'])
    })

    const handleClone = async () => {
        await onAddVersion(schema)
        onClose()
    }

    const handleSave = async () => {
        await onSave()
        await invalidateSchemaData()
        onClose()
    }

    const onSaveAndSubmit = async () => {
        await onSave()
        await executeSendApprovalToReview(approval)
        onClose()
    }

    const onSubmitBackToDraft = async () => {
        await executeBackToDraft(approval)
        onClose()
    }

    const renderModalContent = () => {
        // if (schema?.versioning.isHead && !approval && (schema.isDefault)) {
        //     return (
        //         <>
        //             <DialogContent sx={{ pb: 2 }}>
        //                 <Box mb={3}>
        //                     <Typography variant='body1' gutterBottom>
        //                         The default version of the schema cannot be modified, create a new schema and ask for approval and review to send it live
        //                     </Typography>
        //                 </Box>
        //             </DialogContent>
        //         </>
        //     )
        // }

        const lastChange = approval?.changes?.length ? approval?.changes[approval?.changes.length - 1] : null

        if (!approval) {
            return (
                <>
                    <DialogTitle sx={{ pb: 1 }}>
                        <Box display='flex' justifyContent='space-between' alignItems='center'>
                            <Typography variant='h6'>Modify Default Schema</Typography>
                            <IconButton onClick={onClose} size='small'>
                                <Close />
                            </IconButton>
                        </Box>
                    </DialogTitle>
                    <DialogContent>
                        <Typography>You are editing the default version. Save your changes below.</Typography>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 3 }}>
                        <LoadingButton
                            startIcon={<SaveRounded />}
                            variant='contained'
                            onClick={handleSave}
                            loading={saveStatus === 'loading'}>
                            Save Changes
                        </LoadingButton>
                    </DialogActions>
                </>
            )
        }

        switch (approval?.status) {
            case 'draft':
                return (
                    <>
                        <DialogTitle sx={{ pb: 1 }}>
                            <Box display='flex' justifyContent='space-between' alignItems='center'>
                                <Typography variant='h6'>Send {approval?.entityType} for review</Typography>
                                <IconButton onClick={onClose} size='small'>
                                    <Close />
                                </IconButton>
                            </Box>
                        </DialogTitle>
                        <DialogContent sx={{ pb: 2 }}>
                            <Stack spacing={2} mt={3}>
                                <Box>
                                    <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                            <Stack spacing={2}>
                                                {ApprovalManager.statusField(approval.status)}
                                                {ApprovalManager.infoField('Entity Name', approval.entityName)}
                                            </Stack>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Stack spacing={2}>
                                                {ApprovalManager.infoField('Drafted By', lastChange.userName ?? 'N/A')}
                                                {ApprovalManager.infoField('Drafted At', Formatter.date(new Date(lastChange.changedAt), 'mm/dd/yyyy hh:mm:ss'))}
                                            </Stack>
                                        </Grid>
                                    </Grid>
                                </Box>
                                <Typography variant='body1'>
                                    Change the status to
                                    {' '}
                                    <Box component='span' sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontWeight: 'bold' }}>
                                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: 'info.main', display: 'inline-block' }} />
                                        Ready for review
                                    </Box>
                                    {' '}
                                    and submit it for approval?
                                </Typography>
                                <Typography variant='subtitle2' color='text.secondary'>
                                    Select Approval Group:
                                </Typography>
                                <Select
                                    fullWidth
                                    required
                                    size='small'
                                    value={selectedGroupUUID ?? ''}
                                    onChange={e => setSelectedGroupUUID(e.target.value)}>
                                    {groups?.map((group: IUserRole) => (
                                        <MenuItem key={group?.uuid} value={group?.uuid}>
                                            {group?.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Stack>
                        </DialogContent>
                        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
                            <LoadingButton
                                loading={saveStatus === 'loading'}
                                loadingPosition='start'
                                startIcon={<SaveRounded />}
                                variant='outlined'
                                onClick={handleSave}
                                sx={{ textTransform: 'none' }}
                                disabled={isSubmitting}>
                                Save Only
                            </LoadingButton>
                            <LoadingButton
                                startIcon={<SendRounded />}
                                variant='contained'
                                loading={statusRequestedApproval === 'loading'}
                                loadingPosition='start'
                                onClick={onSaveAndSubmit}
                                sx={{ textTransform: 'none' }}
                                disabled={isSubmitting || !selectedGroupUUID}>
                                Send For Review
                            </LoadingButton>
                        </DialogActions>
                    </>
                )

            case 'ready-for-review':
                return (
                    <>
                        <DialogTitle sx={{ pb: 1 }}>
                            <Box display='flex' justifyContent='space-between' alignItems='center'>
                                <Typography variant='h6'>Waiting for approval</Typography>
                                <IconButton onClick={onClose} size='small'>
                                    <Close />
                                </IconButton>
                            </Box>
                        </DialogTitle>
                        <DialogContent sx={{ pb: 2 }}>
                            <Stack spacing={2} mt={2}>
                                {ApprovalManager.statusField(approval.status)}
                                {ApprovalManager.infoField('Contract Name', approval.entityName)}
                                {ApprovalManager.infoField('Requested By', lastChange.userName ?? 'N/A')}
                                {ApprovalManager.infoField('Requested At', Formatter.date(new Date(lastChange.changedAt), 'mm/dd/yyyy hh:mm:ss'))}
                            </Stack>
                        </DialogContent>

                        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
                            <LoadingButton
                                startIcon={<SendRounded />}
                                variant='contained'
                                loading={statusBackToDraftApproval === 'loading'}
                                onClick={onSubmitBackToDraft}
                                loadingPosition='start'
                                sx={{ textTransform: 'none' }}
                                disabled={isSubmitting}>
                                Back To Draft
                            </LoadingButton>
                            <Button
                                variant='contained'
                                color='success'
                                onClick={() => dispatch(changeRoute({ route: 'approvals' }))}
                                sx={{ textTransform: 'none' }}
                                disabled={isSubmitting}>
                                Approval Hub
                            </Button>
                        </DialogActions>
                    </>
                )

            case 'rejected':
                return (
                    <>
                        <DialogTitle sx={{ pb: 1 }}>
                            <Box display='flex' justifyContent='space-between' alignItems='center'>
                                <Typography variant='h6'>Review {approval?.entityType} for approval</Typography>
                                <IconButton onClick={onClose} size='small'>
                                    <Close />
                                </IconButton>
                            </Box>
                        </DialogTitle>
                        <DialogContent sx={{ pb: 2 }}>
                            <Stack direction='row' spacing={4} mt={2}>
                                <Stack spacing={2} mt={2} flex={1}>
                                    {ApprovalManager.statusField(approval.status)}
                                    {ApprovalManager.infoField('Contract Name', approval.entityName)}
                                    {ApprovalManager.infoField('Contract Version', schema?.versioning?.version)}
                                </Stack>
                                <Stack spacing={2} mt={2} flex={1}>
                                    {ApprovalManager.infoField('Rejected By', lastChange.userName ?? 'N/A')}
                                    {ApprovalManager.infoField('Rejected At', Formatter.date(new Date(lastChange.changedAt), 'mm/dd/yyyy hh:mm:ss'))}
                                </Stack>
                            </Stack>
                        </DialogContent>

                        {lastChange?.description && (
                            <DialogContent sx={{ pb: 2 }}>
                                <Box>
                                    <Typography variant='subtitle2' color='text.secondary'>
                                        Rejection Reason
                                    </Typography>
                                    <Box
                                        sx={{
                                            mt: 1.5,
                                            p: 2,
                                            border: theme.palette.divider,
                                            borderRadius: '4px',
                                            backgroundColor: 'dimgray',
                                            minHeight: '200px',
                                            maxHeight: '250px',
                                            overflow: 'auto'
                                        }}>
                                        <Typography
                                            variant='body1'
                                            component='pre'
                                            sx={{
                                                whiteSpace: 'pre-wrap',
                                                wordWrap: 'break-word',
                                                margin: 0,
                                                fontFamily: 'inherit',
                                                fontSize: '1rem',
                                                lineHeight: 1.4375
                                            }}>
                                            {lastChange.description}
                                        </Typography>
                                    </Box>
                                </Box>
                            </DialogContent>
                        )}

                        <DialogContent sx={{ pb: 2 }}>
                            <Stack spacing={2} mt={3}>
                                <Typography variant='subtitle2' color='text.secondary'>
                                    Select Approval Group:
                                </Typography>
                                <Select
                                    fullWidth
                                    required
                                    size='small'
                                    value={selectedGroupUUID ?? ''}
                                    onChange={e => setSelectedGroupUUID(e.target.value)}>
                                    {groups?.map((group: IUserRole) => (
                                        <MenuItem key={group?.uuid} value={group?.uuid}>
                                            {group?.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Stack>
                            <Stack spacing={2} mt={3}>
                                <Typography variant='body1' gutterBottom>
                                    Do you want to resend to review again?
                                </Typography>
                            </Stack>
                        </DialogContent>
                        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
                            <LoadingButton
                                startIcon={(
                                    <Badge
                                        variant='dot'
                                        color='warning'
                                        invisible={!isDirty}>
                                        <Save />
                                    </Badge>
                                )}
                                variant='outlined'
                                onClick={handleSave}
                                sx={{ textTransform: 'none' }}
                                loadingPosition='start'
                                loading={saveStatus === 'loading'}
                                disabled={isSubmitting}>
                                Save Only
                            </LoadingButton>
                            <LoadingButton
                                startIcon={<SendRounded />}
                                variant='contained'
                                loading={statusRequestedApproval === 'loading'}
                                onClick={onSaveAndSubmit}
                                loadingPosition='start'
                                sx={{ textTransform: 'none' }}
                                disabled={isSubmitting || !selectedGroupUUID}>
                                Send For Review
                            </LoadingButton>
                        </DialogActions>
                    </>
                )

            case 'approved':
                return (
                    <>
                        <DialogTitle sx={{ pb: 1 }}>
                            <Box display='flex' justifyContent='space-between' alignItems='center'>
                                <Typography variant='h6' textTransform='capitalize'>{approval?.entityType} details</Typography>
                                <IconButton onClick={onClose} size='small'>
                                    <Close />
                                </IconButton>
                            </Box>
                        </DialogTitle>
                        <DialogContent sx={{ pb: 2 }}>
                            <Grid container spacing={2} mt={3}>
                                <Grid item xs={6}>
                                    <Stack spacing={2}>
                                        {ApprovalManager.statusField(approval.status)}
                                        {ApprovalManager.infoField('Schema Name', approval.entityName)}
                                    </Stack>
                                </Grid>
                                <Grid item xs={6}>
                                    <Stack spacing={2}>
                                        {ApprovalManager.infoField('Approved By', lastChange.userName ?? 'N/A')}
                                        {ApprovalManager.infoField('Approved At', Formatter.date(new Date(lastChange.changedAt), 'mm/dd/yyyy hh:mm:ss'))}
                                    </Stack>
                                </Grid>
                            </Grid>
                        </DialogContent>
                        <DialogContent sx={{ pb: 2 }}>
                            <Box mb={3}>
                                <Typography variant='body1' gutterBottom>
                                    The current approved version will remain live until the new version is approved and published.
                                </Typography>
                            </Box>
                        </DialogContent>
                        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
                            <LoadingButton
                                startIcon={<CopyAllRounded />}
                                loading={statusAddVersion === 'loading'}
                                loadingPosition='start'
                                variant='contained'
                                onClick={handleClone}
                                sx={{ textTransform: 'none' }}
                                disabled={isSubmitting}>
                                Create New Version
                            </LoadingButton>
                        </DialogActions>
                    </>
                )
        }
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth PaperProps={{ sx: { borderRadius: 2, minHeight: 300 } }}>
            {renderModalContent()}
        </Dialog>
    )
}

export default SchemaSubmitModal
